// Centralised notification helpers: preferences, sound, vibration, native notifications.
const KEY = "fd_notif_prefs";
const SOUNDS_KEY = "fd_notif_sounds"; // map slot -> data URL (custom uploaded sounds)

// Event categories that can each have their own sound.
export const NOTIF_CATEGORIES = [
  { key: "ticket", label: "Ticket", desc: "Nouveau ticket ou réponse client." },
  { key: "validation", label: "Validation de livrable", desc: "Le client accepte ou refuse une livraison." },
  { key: "chat", label: "Chat / Message", desc: "Nouveau message dans une discussion." },
  { key: "email", label: "Email", desc: "Nouvel email reçu." },
];

// Preset chimes (synthesised via WebAudio, no assets needed).
export const SOUND_PRESETS = [
  { id: "chime", label: "Carillon" },
  { id: "ding", label: "Ding" },
  { id: "pop", label: "Pop" },
  { id: "bell", label: "Cloche" },
  { id: "marimba", label: "Marimba" },
  { id: "alert", label: "Alerte" },
  { id: "custom", label: "Son personnalisé…" },
];

export const DEFAULT_PREFS = {
  sound: true,
  vibration: true,
  desktop: true,
  perEvent: false,            // false = un seul son pour tout / true = un son par type
  sound_all: "chime",         // son unique quand perEvent = false
  sounds: { ticket: "bell", validation: "marimba", chat: "pop", email: "ding" },
};

export function getNotifPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY)) || {};
    return { ...DEFAULT_PREFS, ...raw, sounds: { ...DEFAULT_PREFS.sounds, ...(raw.sounds || {}) } };
  } catch { return { ...DEFAULT_PREFS, sounds: { ...DEFAULT_PREFS.sounds } }; }
}
export function setNotifPrefs(p) {
  localStorage.setItem(KEY, JSON.stringify(p));
  window.dispatchEvent(new CustomEvent("fd:notif-prefs"));
}

// ---- custom uploaded sounds (data URLs kept in a separate key) ----
export function getCustomSounds() {
  try { return JSON.parse(localStorage.getItem(SOUNDS_KEY)) || {}; } catch { return {}; }
}
export function setCustomSound(slot, dataUrl) {
  const all = getCustomSounds();
  if (dataUrl) all[slot] = dataUrl; else delete all[slot];
  localStorage.setItem(SOUNDS_KEY, JSON.stringify(all));
}

// Synthesised preset definitions: sequence of tones.
const PRESET_SPECS = {
  chime: { type: "sine", tones: [[880, 0], [1320, 0.12]], dur: 0.36, gain: 0.18 },
  ding: { type: "sine", tones: [[1568, 0]], dur: 0.5, gain: 0.2 },
  pop: { type: "triangle", tones: [[440, 0], [780, 0.04]], dur: 0.16, gain: 0.22 },
  bell: { type: "sine", tones: [[1046, 0], [1568, 0.02]], dur: 0.9, gain: 0.16 },
  marimba: { type: "triangle", tones: [[659, 0], [988, 0.09], [1319, 0.18]], dur: 0.4, gain: 0.2 },
  alert: { type: "square", tones: [[660, 0], [660, 0.16], [660, 0.32]], dur: 0.14, gain: 0.13 },
};

function playPreset(id) {
  const spec = PRESET_SPECS[id] || PRESET_SPECS.chime;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    spec.tones.forEach(([freq, at]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = spec.type;
      const t0 = ctx.currentTime + at;
      o.frequency.setValueAtTime(freq, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(spec.gain, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + spec.dur);
      o.start(t0); o.stop(t0 + spec.dur + 0.02);
    });
  } catch {}
}

function playCustom(slot) {
  const url = getCustomSounds()[slot];
  if (!url) { playPreset("chime"); return; }
  try { const a = new Audio(url); a.volume = 0.9; a.play().catch(() => {}); } catch {}
}

// Resolve which sound to play for a given category (respects perEvent toggle).
function soundIdFor(prefs, category) {
  if (!prefs.perEvent) return { id: prefs.sound_all || "chime", slot: "all" };
  return { id: (prefs.sounds || {})[category] || "chime", slot: category };
}

export function playSoundFor(category = "email") {
  const prefs = getNotifPrefs();
  const { id, slot } = soundIdFor(prefs, category);
  if (id === "custom") playCustom(slot); else playPreset(id);
}

// Preview a specific sound id (used in settings). slot needed for custom.
export function previewSound(id, slot) {
  if (id === "custom") playCustom(slot); else playPreset(id);
}

// Back-compat: generic chime.
export function playChime() { playPreset("chime"); }

export function vibrate(pattern = [90, 40, 90]) {
  try { if (navigator.vibrate) navigator.vibrate(pattern); } catch {}
}

export function notifSupported() { return typeof window !== "undefined" && "Notification" in window; }
export function notifPermission() { return notifSupported() ? Notification.permission : "unsupported"; }

export async function requestNotifPermission() {
  if (!notifSupported()) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  try { return await Notification.requestPermission(); } catch { return "denied"; }
}

async function showDesktopNotification(title, body) {
  if (!notifSupported() || Notification.permission !== "granted") return;
  const opts = { body: body || "", icon: "/brand/flowdesk-mark.png", badge: "/brand/flowdesk-mark.png", tag: "flowdesk", renotify: true };
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.ready) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, opts);
    } else {
      new Notification(title, opts);
    }
  } catch {
    try { new Notification(title, opts); } catch {}
  }
}

// Fires the enabled side-effects (sound / vibration / native notification). Toast is handled separately in-app.
export function fireNotification(title, body, category = "email") {
  const p = getNotifPrefs();
  if (p.sound) playSoundFor(category);
  if (p.vibration) vibrate();
  if (p.desktop) showDesktopNotification(title, body);
}
